import {
  faArrowsToCircle,
  faCopy,
  faExternalLink,
} from "@fortawesome/pro-solid-svg-icons";
import { MultiButton, SlimButton } from "components/Button";
import {
  openCreateExAgentSaleModal,
  openCreateSellViaExAgentSaleModal,
} from "lib/navigation";
import {
  useCanCreateSellViaExAgentSaleFromSale,
  useHasCreateSellViaExAgentSaleFeature,
} from "hooks";
import React from "react";
import { useSelector } from "react-redux";
import {
  getIsOnline,
  selectCanCreateExternalAgentSaleFromSale,
} from "selectors";
import { LivestockSaleAgGridRowType } from "selectors/aggrid/sales";

interface LivestockSaleActionsColumnRendererProps {
  data: LivestockSaleAgGridRowType;
  context: {
    handleOpenSale: (livestockSaleId: number) => void;
    canResolveTemporaryBusinesses?: boolean;
    handleResolveTemporaryBusinesses?: (livestockSaleId: number) => void;
  };
}

export function LivestockSaleActionsColumnRenderer(
  props: LivestockSaleActionsColumnRendererProps,
): React.JSX.Element {
  const { data, context } = props;

  const {
    handleOpenSale,
    canResolveTemporaryBusinesses,
    handleResolveTemporaryBusinesses,
  } = context;

  const isOnline = useSelector(getIsOnline);

  const canCreateExternalAgentSaleFromSale = useSelector(
    selectCanCreateExternalAgentSaleFromSale,
  );

  const hasCreateSellViaExternalAgentSaleFeature =
    useHasCreateSellViaExAgentSaleFeature();
  const canCreateSellViaExternalAgentSaleFromSale =
    useCanCreateSellViaExAgentSaleFromSale(data?.livestockSale.id);

  if (!data) {
    return null;
  }

  const unresolvedBusinessCount =
    data.livestockSale.unresolvedBusinessCount ?? 0;

  const onOpenSaleClick = () => {
    handleOpenSale(data.livestockSale.id);
  };

  const onCreateExternalAgentSaleFromSale = () => {
    openCreateExAgentSaleModal(data.livestockSale.id);
  };

  const buttons = [
    {
      onClick: onOpenSaleClick,
      dataTour: `action-open-sale:${data.livestockSale.id}`,
      icon: faExternalLink,
      title: " Open",
    },
    canCreateExternalAgentSaleFromSale && {
      onClick: onCreateExternalAgentSaleFromSale,
      dataTour: `action-create-ex-agent-sale:${data.livestockSale.id}`,
      icon: faCopy,
      title: " Create Ex Agent Sale",
      isDisabled: !isOnline,
    },
    hasCreateSellViaExternalAgentSaleFeature && {
      onClick: () => openCreateSellViaExAgentSaleModal(data.livestockSale.id),
      dataTour: `action-create-sell-via-ex-agent-sale:${data.livestockSale.id}`,
      icon: faCopy,
      title: " Create Sell via Ex Agent Sale",
      isDisabled: !isOnline || !canCreateSellViaExternalAgentSaleFromSale,
      tooltip: canCreateSellViaExternalAgentSaleFromSale
        ? undefined
        : "Only available for saleyard sales, or sales you are consigning to",
    },
    canResolveTemporaryBusinesses && {
      onClick: () => handleResolveTemporaryBusinesses?.(data.livestockSale.id),
      dataTour: `action-resolve-temporary-businesses:${data.livestockSale.id}`,
      icon: faArrowsToCircle,
      title: ` Resolve Temporary Businesses (${unresolvedBusinessCount})`,
      isDisabled: unresolvedBusinessCount === 0,
    },
  ].filter(Boolean);

  return <MultiButton buttons={buttons} ButtonComponent={SlimButton} />;
}
